import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { formatTime, formatHours } from '../lib/utils'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { Timer, Utensils, Play, Square, Coffee, Edit3, AlertTriangle } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function AyunoPage() {
  const { t, locale } = useLanguage()
  const [activeSession, setActiveSession] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [showEndModal, setShowEndModal] = useState(false)
  const [showEditStartModal, setShowEditStartModal] = useState(false)
  const [mealQuality, setMealQuality] = useState('')
  const [notes, setNotes] = useState('')
  
  // Edit fasting start state
  const [editStartDate, setEditStartDate] = useState('')
  const [editStartTime, setEditStartTime] = useState('')
  const [showConfirm48h, setShowConfirm48h] = useState(false)
  
  // Edit eating start state
  const [showEditEatModal, setShowEditEatModal] = useState(false)
  const [editEatDate, setEditEatDate] = useState('')
  const [editEatTime, setEditEatTime] = useState('')
  const [showConfirm48hEat, setShowConfirm48hEat] = useState(false)
  
  const intervalRef = useRef(null)

  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fasting/active`)
      setActiveSession(response.data)
    } catch (error) {
      console.error('Error fetching session:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchActiveSession()
  }, [fetchActiveSession])

  useEffect(() => {
    if (activeSession) {
      const calculateElapsed = () => {
        let startTime
        if (activeSession.start_eat_at && !activeSession.end_eat_at) {
          startTime = new Date(activeSession.start_eat_at).getTime()
        } else if (activeSession.start_fast_at && !activeSession.end_fast_at) {
          startTime = new Date(activeSession.start_fast_at).getTime()
        } else {
          return 0
        }
        return Math.floor((Date.now() - startTime) / 1000)
      }

      setElapsedSeconds(calculateElapsed())
      
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(calculateElapsed())
      }, 1000)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [activeSession])

  const getSessionState = () => {
    if (!activeSession) return 'idle'
    if (activeSession.start_eat_at && !activeSession.end_eat_at) return 'eating'
    if (activeSession.start_fast_at && !activeSession.end_fast_at) return 'fasting'
    return 'idle'
  }

  const state = getSessionState()
  const dateLocale = locale === 'es-MX' ? es : enUS

  const handleStartFasting = async () => {
    setActionLoading(true)
    try {
      const response = await axios.post(`${API}/fasting`)
      setActiveSession(response.data)
      toast.success(t('fasting.fastStarted'))
    } catch (error) {
      const message = error.response?.data?.detail || t('common.error')
      toast.error(message)
    } finally {
      setActionLoading(false)
    }
  }

  const handleBreakFast = async () => {
    if (!activeSession) return
    setActionLoading(true)
    try {
      const response = await axios.put(`${API}/fasting/${activeSession.id}/break`)
      setActiveSession(response.data)
      toast.success(locale === 'es-MX' ? '¡Ayuno roto! Comienza a comer.' : 'Fast broken! Start eating.')
    } catch (error) {
      toast.error(t('fasting.errorSaving'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleEndEating = async () => {
    if (!activeSession || !mealQuality) {
      toast.error(t('fasting.selectMealQuality'))
      return
    }
    setActionLoading(true)
    try {
      const params = new URLSearchParams({ meal_quality: mealQuality })
      if (notes) params.append('notes', notes)
      
      const response = await axios.put(
        `${API}/fasting/${activeSession.id}/end-eating?${params.toString()}`
      )
      setActiveSession(response.data)
      setShowEndModal(false)
      setMealQuality('')
      setNotes('')
      toast.success(locale === 'es-MX' ? '¡Nuevo ayuno iniciado!' : 'New fast started!')
    } catch (error) {
      toast.error(t('fasting.errorSaving'))
    } finally {
      setActionLoading(false)
    }
  }

  const openEditStartModal = () => {
    if (activeSession) {
      const startDate = parseISO(activeSession.start_fast_at)
      setEditStartDate(format(startDate, 'yyyy-MM-dd'))
      setEditStartTime(format(startDate, 'HH:mm'))
      setShowConfirm48h(false)
    }
    setShowEditStartModal(true)
  }

  const handleEditStart = async (confirmOver48h = false) => {
    if (!activeSession || !editStartDate || !editStartTime) {
      toast.error(locale === 'es-MX' ? 'Selecciona fecha y hora' : 'Select date and time')
      return
    }
    
    const newStartDateTime = new Date(`${editStartDate}T${editStartTime}:00`)
    const now = new Date()
    
    // Client-side validation
    if (newStartDateTime > now) {
      toast.error(locale === 'es-MX' ? 'El inicio no puede ser en el futuro' : 'Start cannot be in the future')
      return
    }
    
    setActionLoading(true)
    try {
      const response = await axios.put(`${API}/fasting/${activeSession.id}/edit-start`, {
        new_start_fast_at: newStartDateTime.toISOString(),
        confirm_over_48h: confirmOver48h
      })
      
      setActiveSession(response.data)
      setShowEditStartModal(false)
      setShowConfirm48h(false)
      toast.success('Inicio de ayuno actualizado')
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al actualizar'
      
      // Check if it requires 48h confirmation
      if (detail.includes('48 horas')) {
        setShowConfirm48h(true)
      } else {
        toast.error(detail)
      }
    } finally {
      setActionLoading(false)
    }
  }

  // Open edit eating start modal
  const openEditEatModal = () => {
    if (activeSession?.start_eat_at) {
      const eatDate = parseISO(activeSession.start_eat_at)
      setEditEatDate(format(eatDate, 'yyyy-MM-dd'))
      setEditEatTime(format(eatDate, 'HH:mm'))
      setShowConfirm48hEat(false)
    }
    setShowEditEatModal(true)
  }

  // Handle edit eating start
  const handleEditEatStart = async (confirmOver48h = false) => {
    if (!activeSession || !editEatDate || !editEatTime) {
      toast.error('Selecciona fecha y hora')
      return
    }
    
    const newEatDateTime = new Date(`${editEatDate}T${editEatTime}:00`)
    const now = new Date()
    
    // Client-side validation
    if (newEatDateTime > now) {
      toast.error('El inicio de comida no puede ser en el futuro')
      return
    }
    
    // Must be after fasting start
    const fastStart = parseISO(activeSession.start_fast_at)
    if (newEatDateTime <= fastStart) {
      toast.error('El inicio de comida debe ser posterior al inicio del ayuno')
      return
    }
    
    setActionLoading(true)
    try {
      const response = await axios.put(`${API}/fasting/${activeSession.id}/edit-eat-start`, {
        new_start_eat_at: newEatDateTime.toISOString(),
        confirm_over_48h: confirmOver48h
      })
      
      setActiveSession(response.data)
      setShowEditEatModal(false)
      setShowConfirm48hEat(false)
      toast.success('Inicio de comida actualizado')
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al actualizar'
      
      // Check if it requires 48h confirmation
      if (detail.includes('48 horas')) {
        setShowConfirm48hEat(true)
      } else {
        toast.error(detail)
      }
    } finally {
      setActionLoading(false)
    }
  }

  const progress = Math.min((elapsedSeconds / (16 * 3600)) * 100, 100)
  const circumference = 2 * Math.PI * 120

  const strokeDashoffset = circumference - (progress / 100) * circumference

  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 pb-safe max-w-md mx-auto">
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('fasting.title')}</h1>
        <p className="text-muted-foreground text-sm">
          {state === 'idle' && (locale === 'es-MX' ? 'Inicia tu período de ayuno' : 'Start your fasting period')}
          {state === 'fasting' && (locale === 'es-MX' ? 'Estás en ayuno' : "You're fasting")}
          {state === 'eating' && (locale === 'es-MX' ? 'Ventana de alimentación' : 'Eating window')}
        </p>
      </div>

      {/* Timer Circle */}
      <div className="flex justify-center mb-8">
        <div className="relative" data-testid="timer-circle">
          <svg width="280" height="280" className="transform -rotate-90">
            <circle
              cx="140"
              cy="140"
              r="120"
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth="12"
            />
            <circle
              cx="140"
              cy="140"
              r="120"
              fill="none"
              stroke={state === 'eating' ? '#10b981' : state === 'fasting' ? '#8b5cf6' : 'hsl(var(--muted))'}
              strokeWidth="12"
              strokeDasharray={circumference}
              strokeDashoffset={state === 'idle' ? circumference : strokeDashoffset}
              className="timer-ring"
            />
          </svg>
          
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {state === 'idle' ? (
              <Timer className="w-12 h-12 text-muted-foreground mb-2" />
            ) : state === 'eating' ? (
              <Utensils className="w-12 h-12 text-emerald-500 mb-2" />
            ) : (
              <Coffee className="w-12 h-12 text-violet-500 mb-2" />
            )}
            
            <span className="text-4xl font-bold font-mono timer-display" data-testid="timer-display">
              {state === 'idle' ? '00:00:00' : formatTime(elapsedSeconds)}
            </span>
            
            <span className="text-sm text-muted-foreground mt-1">
              {state === 'idle' && (locale === 'es-MX' ? 'Sin actividad' : 'No activity')}
              {state === 'fasting' && t('fasting.fasting')}
              {state === 'eating' && t('fasting.eating')}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        {state === 'idle' && (
          <Button
            onClick={handleStartFasting}
            disabled={actionLoading}
            className="w-full h-14 text-lg font-bold rounded-full bg-violet-600 hover:bg-violet-700"
            data-testid="start-fasting-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            {t('fasting.startFast')}
          </Button>
        )}

        {state === 'fasting' && (
          <>
            <Button
              onClick={handleBreakFast}
              disabled={actionLoading}
              className="w-full h-14 text-lg font-bold rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="break-fast-btn"
            >
              <Utensils className="w-5 h-5 mr-2" />
              {t('fasting.startEating')}
            </Button>
            
            <Button
              onClick={openEditStartModal}
              variant="outline"
              className="w-full h-12 rounded-full"
              data-testid="edit-start-btn"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t('fasting.editStartTime')}
            </Button>
          </>
        )}

        {state === 'eating' && (
          <>
            <Button
              onClick={() => setShowEndModal(true)}
              disabled={actionLoading}
              className="w-full h-14 text-lg font-bold rounded-full bg-violet-600 hover:bg-violet-700"
              data-testid="end-eating-btn"
            >
              {t('fasting.endEating')}
            </Button>
            
            <Button
              onClick={openEditEatModal}
              variant="outline"
              className="w-full h-12 rounded-full"
              data-testid="edit-eat-start-btn"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t('fasting.editEatTime')}
            </Button>
          </>
        )}
      </div>

      {/* Session Info */}
      {activeSession && state !== 'idle' && (
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-muted-foreground">{locale === 'es-MX' ? 'Inicio ayuno' : 'Fast start'}</p>
                  {activeSession.start_edited && (
                    <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 border-amber-500/30">
                      {locale === 'es-MX' ? 'Editado' : 'Edited'}
                    </Badge>
                  )}
                </div>
                <p className="font-medium">
                  {format(parseISO(activeSession.start_fast_at), "d MMM, HH:mm", { locale: dateLocale })}
                </p>
                {activeSession.start_edited && activeSession.original_start_fast_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Original: {format(parseISO(activeSession.original_start_fast_at), "HH:mm", { locale: dateLocale })}
                  </p>
                )}
              </div>
              {activeSession.fast_hours && (
                <div>
                  <p className="text-muted-foreground">{t('fasting.fastingHours')}</p>
                  <p className="font-medium">{formatHours(activeSession.fast_hours)}</p>
                </div>
              )}
              {activeSession.start_eat_at && (
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground">{locale === 'es-MX' ? 'Inicio comida' : 'Meal start'}</p>
                    {activeSession.eat_start_edited && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 border-amber-500/30">
                        {locale === 'es-MX' ? 'Editado' : 'Edited'}
                      </Badge>
                    )}
                  </div>
                  <p className="font-medium">
                    {format(parseISO(activeSession.start_eat_at), "d MMM, HH:mm", { locale: dateLocale })}
                  </p>
                  {activeSession.eat_start_edited && activeSession.original_start_eat_at && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Original: {format(parseISO(activeSession.original_start_eat_at), "HH:mm", { locale: dateLocale })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Start Modal */}
      <Dialog open={showEditStartModal} onOpenChange={setShowEditStartModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar inicio de ayuno</DialogTitle>
            <DialogDescription>
              Corrige el inicio real si lo comenzaste antes.
            </DialogDescription>
          </DialogHeader>
          
          {!showConfirm48h ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    data-testid="edit-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    data-testid="edit-start-time"
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                El inicio no puede ser en el futuro ni posterior al fin del ayuno (si existe).
              </p>
            </div>
          ) : (
            <div className="py-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-600">¿Estás seguro?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    El inicio que seleccionaste es más de 48 horas atrás. Confirma si es correcto.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditStartModal(false)
                setShowConfirm48h(false)
              }}
            >
              Cancelar
            </Button>
            {!showConfirm48h ? (
              <Button 
                onClick={() => handleEditStart(false)} 
                disabled={actionLoading}
                data-testid="save-edit-start-btn"
              >
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </Button>
            ) : (
              <Button 
                onClick={() => handleEditStart(true)} 
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-48h-btn"
              >
                {actionLoading ? 'Confirmando...' : 'Sí, confirmar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Eating Start Modal */}
      <Dialog open={showEditEatModal} onOpenChange={(open) => {
        setShowEditEatModal(open)
        if (!open) setShowConfirm48hEat(false)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="w-5 h-5" />
              Editar inicio de comida
            </DialogTitle>
            <DialogDescription>
              Ajusta la hora en que empezaste a comer
            </DialogDescription>
          </DialogHeader>

          {!showConfirm48hEat ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={editEatDate}
                    onChange={(e) => setEditEatDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    data-testid="edit-eat-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={editEatTime}
                    onChange={(e) => setEditEatTime(e.target.value)}
                    data-testid="edit-eat-time"
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                El inicio de comida debe ser posterior al inicio del ayuno y no puede ser en el futuro.
              </p>
            </div>
          ) : (
            <div className="py-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-600">¿Estás seguro?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    El inicio que seleccionaste es más de 48 horas atrás. Confirma si es correcto.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditEatModal(false)
                setShowConfirm48hEat(false)
              }}
            >
              Cancelar
            </Button>
            {!showConfirm48hEat ? (
              <Button 
                onClick={() => handleEditEatStart(false)} 
                disabled={actionLoading}
                data-testid="save-edit-eat-btn"
              >
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </Button>
            ) : (
              <Button 
                onClick={() => handleEditEatStart(true)} 
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-48h-eat-btn"
              >
                {actionLoading ? 'Confirmando...' : 'Sí, confirmar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Eating Modal */}
      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Qué tan sano comiste hoy?</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Calidad de la comida *</Label>
              <Select value={mealQuality} onValueChange={setMealQuality}>
                <SelectTrigger data-testid="meal-quality-select">
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bien">Bien - Comí saludable</SelectItem>
                  <SelectItem value="Regular">Regular - Pudo ser mejor</SelectItem>
                  <SelectItem value="Mal">Mal - Comí poco saludable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                placeholder="¿Qué comiste? Observaciones..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="meal-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleEndEating} 
              disabled={actionLoading || !mealQuality}
              data-testid="confirm-end-eating-btn"
            >
              Confirmar e Iniciar Ayuno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
